import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import * as fromRoot from 'src/app/state/app.state';
import { Project } from 'src/app/shared/models';
import { Holiday, HolidayType } from 'src/app/shared/models/billing-settings';
import { ConfirmDialogComponent, ConfirmDialogModel } from 'src/app/shared/components';
import { HolidayDialogComponent, HolidayDialogResult } from 'src/app/shared/components/holiday-dialog/holiday-dialog.component';
import {
  FetchBillingSettingsRequest,
  UpdateBillingSettingsRequest,
  FetchPayAppTemplatesRequest,
  PreviewPayAppTemplateRequest,
  FetchProjectCalendarRequest,
  UpdateProjectCalendarRequest,
  InitializeProjectCalendarRequest,
} from 'src/app/modules/projects/core/projects.actions';
import {
  selectBillingSettings,
  selectPayAppTemplates,
  selectPayAppTemplatesLoading,
  selectProjectCalendar,
  selectProjectCalendarLoading,
} from 'src/app/modules/projects/core/projects.selectors';
import { ProjectsService } from 'src/app/modules/projects/core/projects.service';

@Component({
    selector: 'app-project-billing-settings',
    templateUrl: './project-billing-settings.component.html',
    styleUrls: ['./project-billing-settings.component.scss'],
    standalone: false
})
export class ProjectBillingSettingsComponent implements OnInit, OnDestroy {
  @Input() project: Project;
  @Input() isOrgAdmin = false;

  billingForm: UntypedFormGroup;
  loading = false;
  initialized = false;

  // Templates
  templates: any[] = [];
  templatesLoading = false;
  selectedTemplateId: string | null = null;

  contractTypeOptions = [
    { value: 'calendar', label: 'Calendar Days' },
    { value: 'working', label: 'Working Days' },
  ];

  mohOverContractPolicyOptions = [
    { value: 'warning_ack', label: 'Warn and require acknowledgement' },
    { value: 'hard_block', label: 'Block payapp creation' },
  ];

  errorMessages: { [key: string]: string } = {};

  // Calendar
  selectedYear: number = new Date().getFullYear();
  projectHolidays: Holiday[] = [];
  orgHolidays: Holiday[] = [];
  calendarData: any = null;
  calendarLoading = false;
  pendingProjectCalendars: { [year: string]: Holiday[] } | null = null;
  calendarDirty = false;

  holidayTypeOptions: { value: HolidayType; label: string }[] = [
    { value: 'holiday', label: 'Holiday' },
    { value: 'moratorium', label: 'Moratorium' },
    { value: 'weather', label: 'Weather' },
    { value: 'suspension', label: 'Suspension' },
  ];

  private onDestroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store<fromRoot.State>,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private projectsService: ProjectsService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadBillingSettings();
    this.loadTemplates();
    this.loadProjectCalendar();
    this.subscribeToStore();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initForm(): void {
    this.billingForm = this.fb.group({
      use_legacy_payapp_layout: [false],
      include_change_orders: [true],
      retainage_percentage: [5.00, [Validators.required, Validators.min(0), Validators.max(100)]],
      retainage_threshold: [null, [Validators.min(0), Validators.max(100)]],
      reduced_retainage_percentage: [null, [Validators.min(0), Validators.max(100)]],
      liquidated_damages_per_day: [null, [Validators.min(0)]],
      begin_contract_time_date: [null],
      notice_to_proceed_date: [null],
      actual_start_date: [null],
      original_contract_days: [null, [Validators.min(0)]],
      substantial_completion_date: [null],
      contract_type: ['calendar'],
      default_template_id: [null],
      moh_over_contract_policy: ['warning_ack'],
    });
  }

  get useLegacyLayout(): boolean {
    return this.billingForm?.get('use_legacy_payapp_layout')?.value === true;
  }

  templateOptions: { value: any; label: string }[] = [];

  private updateTemplateOptions(): void {
    this.templateOptions = [
      ...this.systemTemplates.map(t => ({ value: t.id, label: t.name })),
      ...this.projectTemplates.map(t => ({ value: t.id, label: `${t.name} — Project` })),
      ...this.orgTemplates.map(t => ({ value: t.id, label: `${t.name} — Organization` })),
    ];
  }

  checkForErrors(controlName: string): void {
    const control = this.billingForm?.get(controlName);
    if (control?.errors) {
      if (control.errors['required']) {
        this.errorMessages[controlName] = 'Required';
      } else if (control.errors['min'] || control.errors['max']) {
        this.errorMessages[controlName] = 'Must be between 0 and 100';
      } else {
        this.errorMessages[controlName] = '';
      }
    } else {
      this.errorMessages[controlName] = '';
    }
  }

  private loadBillingSettings(): void {
    if (this.project?.id) {
      this.loading = true;
      this.store.dispatch(FetchBillingSettingsRequest({
        payload: { projectId: this.project.id },
      }));
    }
  }

  private loadTemplates(): void {
    if (this.project?.id && this.project?.owner_organization_id) {
      // Fetch templates with both org and project IDs to get all available templates
      this.store.dispatch(FetchPayAppTemplatesRequest({
        organizationId: this.project.owner_organization_id,
        projectId: this.project.id,
      }));
    }
  }

  private loadProjectCalendar(): void {
    if (this.project?.id) {
      this.store.dispatch(FetchProjectCalendarRequest({
        payload: { projectId: this.project.id },
      }));
    }
  }

  private subscribeToStore(): void {
    this.store.select(selectBillingSettings)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(settings => {
        this.loading = false;
        this.initialized = true;
        if (settings) {
          this.patchFormValues(settings);
        }
      });

    this.store.select(selectPayAppTemplates)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(templates => {
        this.templates = templates || [];
        this.updateTemplateOptions();
        // If no template is selected and system template is available, default to it
        this.setDefaultTemplateIfNeeded();
      });

    this.store.select(selectPayAppTemplatesLoading)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(loading => {
        this.templatesLoading = loading;
      });

    this.store.select(selectProjectCalendar)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(calendar => {
        this.calendarData = calendar;
        this.updateCalendarHolidays();
      });

    this.store.select(selectProjectCalendarLoading)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(loading => {
        this.calendarLoading = loading;
      });
  }

  private setDefaultTemplateIfNeeded(): void {
    const currentTemplateId = this.billingForm?.get('default_template_id')?.value;
    if (!currentTemplateId && this.systemTemplates.length > 0) {
      const systemDefault = this.systemTemplates[0];
      this.billingForm.patchValue({ default_template_id: systemDefault.id });
      this.selectedTemplateId = systemDefault.id;
    }
  }

  private patchFormValues(settings: any): void {
    // Calculate default contract days from project dates if not set
    let defaultContractDays = settings.original_contract_days;
    if (!defaultContractDays && this.project?.start_date && this.project?.end_date) {
      defaultContractDays = moment(this.project.end_date).diff(moment(this.project.start_date), 'days') + 1;
    }

    // Use project start date as default contract start if not set
    const defaultStartDate = settings.begin_contract_time_date || this.project?.start_date || null;

    this.billingForm.patchValue({
      use_legacy_payapp_layout: settings.use_legacy_payapp_layout ?? false,
      include_change_orders: settings.include_change_orders !== false,
      retainage_percentage: settings.retainage_percentage ?? 5.00,
      retainage_threshold: settings.retainage_threshold,
      reduced_retainage_percentage: settings.reduced_retainage_percentage,
      liquidated_damages_per_day: settings.liquidated_damages_per_day,
      begin_contract_time_date: defaultStartDate,
      notice_to_proceed_date: settings.notice_to_proceed_date || null,
      actual_start_date: settings.actual_start_date || null,
      original_contract_days: defaultContractDays,
      substantial_completion_date: settings.substantial_completion_date,
      contract_type: settings.contract_type ?? 'calendar',
      default_template_id: settings.default_template_id,
      moh_over_contract_policy: settings.moh_over_contract_policy || 'warning_ack',
    });
    this.selectedTemplateId = settings.default_template_id;

    // If templates already loaded, set default if needed
    this.setDefaultTemplateIfNeeded();
  }

  onSave(): void {
    if (this.billingForm.invalid) {
      this.snackBar.open('Please fix validation errors', '', { duration: 3000 });
      return;
    }

    const formValue = this.billingForm.value;
    // Format date to YYYY-MM-DD (API expects date-only, not full ISO timestamp)
    const beginContractDate = formValue.begin_contract_time_date
      ? moment(formValue.begin_contract_time_date).format('YYYY-MM-DD')
      : null;

    const noticeToProceedDate = formValue.notice_to_proceed_date
      ? moment(formValue.notice_to_proceed_date).format('YYYY-MM-DD')
      : null;
    const actualStartDate = formValue.actual_start_date
      ? moment(formValue.actual_start_date).format('YYYY-MM-DD')
      : null;
    const substantialCompletionDate = formValue.substantial_completion_date
      ? moment(formValue.substantial_completion_date).format('YYYY-MM-DD')
      : null;

    const payload = {
      projectId: this.project.id,
      use_legacy_payapp_layout: formValue.use_legacy_payapp_layout ?? false,
      include_change_orders: formValue.include_change_orders !== false,
      retainage_percentage: formValue.retainage_percentage,
      retainage_threshold: formValue.retainage_threshold || null,
      reduced_retainage_percentage: formValue.reduced_retainage_percentage || null,
      liquidated_damages_per_day: formValue.liquidated_damages_per_day || null,
      begin_contract_time_date: beginContractDate,
      notice_to_proceed_date: noticeToProceedDate,
      actual_start_date: actualStartDate,
      original_contract_days: formValue.original_contract_days || null,
      substantial_completion_date: substantialCompletionDate,
      contract_type: formValue.contract_type,
      default_template_id: formValue.default_template_id || null,
      moh_over_contract_policy: formValue.moh_over_contract_policy || 'warning_ack',
    };

    this.store.dispatch(UpdateBillingSettingsRequest({ payload }));
  }

  onReset(): void {
    this.loadBillingSettings();
  }

  previewTemplate(templateId: string): void {
    if (templateId) {
      this.store.dispatch(PreviewPayAppTemplateRequest({ templateId }));
    }
  }

  get canPreview(): boolean {
    const selectedId = this.billingForm?.get('default_template_id')?.value;
    // Can preview any selected template (including system default)
    return !!selectedId;
  }

  openTemplateEditor(template?: any): void {
    // Dynamically import the dialog to avoid circular dependencies
    import('src/app/modules/organization/components/payapp-template-editor-dialog/payapp-template-editor-dialog.component')
      .then(module => {
        const dialogRef = this.dialog.open(module.PayAppTemplateEditorDialogComponent, {
          width: '800px',
          maxHeight: '90vh',
          data: {
            template: template || null,
            organizationId: this.project.owner_organization_id,
            projectId: this.project.id,
            isEdit: !!template,
          },
        });

        dialogRef.afterClosed().subscribe(result => {
          if (result) {
            // Reload templates to get the updated list
            this.loadTemplates();
            // If a new template was created, select it in the dropdown
            if (result.id) {
              this.billingForm.patchValue({ default_template_id: result.id });
            }
          }
        });
      });
  }

  deleteTemplate(template: any): void {
    const message = `Deleting <b>${template.name}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogData = new ConfirmDialogModel('Delete Template?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      // Use correct endpoint based on template type (project-level vs org-level)
      const deleteRequest$ = template.project_id
        ? this.projectsService.deleteProjectPayAppTemplate(this.project.id, template.id)
        : this.projectsService.deletePayAppTemplate(this.project.owner_organization_id, template.id);

      deleteRequest$
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(
          () => {
            this.snackBar.open('Template deleted', '', { duration: 3000 });
            this.loadTemplates();
          },
          (error) => {
            this.snackBar.open(
              error.error?.error || 'Failed to delete template',
              '',
              { duration: 5000 }
            );
          }
        );
    });
  }

  get systemTemplates(): any[] {
    return this.templates?.filter(t => t.is_system || t.scope === 'system') || [];
  }

  get projectTemplates(): any[] {
    return this.templates?.filter(t => t.scope === 'project' || (t.project_id === this.project.id && !t.is_system)) || [];
  }

  get orgTemplates(): any[] {
    return this.templates?.filter(t => t.scope === 'organization' || (!t.project_id && !t.is_system)) || [];
  }

  isSystemTemplate(template: any): boolean {
    return template?.is_system || template?.scope === 'system';
  }

  // Calendar methods
  get isCalendarEmpty(): boolean {
    if (!this.calendarData?.project) return true;
    const yearStr = String(this.selectedYear);
    const yearHolidays = this.calendarData.project[yearStr];
    return !yearHolidays || yearHolidays.length === 0;
  }

  get hasOrgCalendar(): boolean {
    if (!this.calendarData?.org) return false;
    return Object.values(this.calendarData.org).some((holidays: any) => holidays && holidays.length > 0);
  }

  private updateCalendarHolidays(): void {
    if (!this.calendarData) {
      this.projectHolidays = [];
      this.orgHolidays = [];
      return;
    }
    const yearStr = String(this.selectedYear);
    this.projectHolidays = this.calendarData.project?.[yearStr] || [];
    this.orgHolidays = this.calendarData.org?.[yearStr] || [];
  }

  previousYear(): void {
    this.selectedYear--;
    this.updateCalendarHolidays();
  }

  nextYear(): void {
    this.selectedYear++;
    this.updateCalendarHolidays();
  }

  initializeFromOrg(): void {
    if (!this.project?.id) return;

    if (!this.isCalendarEmpty) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: true,
        data: new ConfirmDialogModel(
          'Replace project holidays?',
          'This will replace all existing project holidays with the organization calendar.',
          'Replace',
          'Cancel'
        ),
      });
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.store.dispatch(InitializeProjectCalendarRequest({
            payload: { projectId: this.project.id },
          }));
        }
      });
      return;
    }

    this.store.dispatch(InitializeProjectCalendarRequest({
      payload: { projectId: this.project.id },
    }));
  }

  clearCalendar(): void {
    if (!this.project?.id) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Clear all holidays?',
        'This will remove all project holidays. This cannot be undone.',
        'Clear',
        'Cancel'
      ),
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(UpdateProjectCalendarRequest({
          payload: { projectId: this.project.id, calendars: {} },
        }));
      }
    });
  }

  onHolidayClick(event: { date: string; holiday: Holiday | null }): void {
    // Handled by grid popover
  }

  onHolidayAdd(event: { date: string }): void {
    const dialogRef = this.dialog.open(HolidayDialogComponent, {
      width: '440px',
      maxHeight: '90vh',
      data: { date: event.date, title: 'Add holiday' },
    });

    dialogRef.afterClosed().subscribe((result: HolidayDialogResult) => {
      if (result) {
        this.addHolidayToCalendar({ date: result.date || event.date, name: result.name, type: result.type });
      }
    });
  }

  addHoliday(): void {
    const dialogRef = this.dialog.open(HolidayDialogComponent, {
      width: '440px',
      maxHeight: '90vh',
      data: { showDateField: true, title: 'Add holiday' },
    });

    dialogRef.afterClosed().subscribe((result: HolidayDialogResult) => {
      if (result) {
        const dateStr = moment(result.date).format('YYYY-MM-DD');
        this.addHolidayToCalendar({ date: dateStr, name: result.name, type: result.type });
      }
    });
  }

  private addHolidayToCalendar(holiday: Holiday): void {
    const yearStr = moment(holiday.date).format('YYYY');
    const projectCalendars = { ...(this.calendarData?.project || {}) };
    const yearHolidays = [...(projectCalendars[yearStr] || [])];

    // Remove existing holiday on same date if any
    const filtered = yearHolidays.filter(h => h.date !== holiday.date);
    filtered.push(holiday);
    filtered.sort((a, b) => a.date.localeCompare(b.date));

    projectCalendars[yearStr] = filtered;

    this.store.dispatch(UpdateProjectCalendarRequest({
      payload: {
        projectId: this.project.id,
        calendars: projectCalendars,
      },
    }));
  }

  onHolidayRemove(event: { date?: string; holiday: Holiday; source?: 'project' | 'org' }): void {
    if (event.source === 'org') {
      // For org holidays, add them to project calendar as overrides won't work —
      // just show a snackbar explaining they can't be removed from here
      this.snackBar.open('Org holidays can only be removed from organization settings', '', { duration: 3000 });
      return;
    }
    this.removeHolidayFromCalendar(event.holiday);
  }

  onHolidayEdit(event: { holiday: Holiday; date?: string; source?: 'project' | 'org' }): void {
    const dialogRef = this.dialog.open(HolidayDialogComponent, {
      width: '440px',
      maxHeight: '90vh',
      data: {
        date: event.holiday.date,
        name: event.holiday.name,
        type: event.holiday.type,
        title: 'Edit holiday',
      },
    });

    dialogRef.afterClosed().subscribe((result: HolidayDialogResult) => {
      if (result) {
        this.addHolidayToCalendar({ date: event.holiday.date, name: result.name, type: result.type });
      }
    });
  }

  private removeHolidayFromCalendar(holiday: Holiday): void {
    const yearStr = moment(holiday.date).format('YYYY');
    const projectCalendars = { ...(this.calendarData?.project || {}) };
    const yearHolidays = [...(projectCalendars[yearStr] || [])];

    projectCalendars[yearStr] = yearHolidays.filter(h => h.date !== holiday.date);

    this.store.dispatch(UpdateProjectCalendarRequest({
      payload: {
        projectId: this.project.id,
        calendars: projectCalendars,
      },
    }));
  }
}
